import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Plus, Copy, Edit2, Archive, Send, Loader2, Trash2, Library, Trophy,
  ListChecks, FileText, History, BookOpen, Zap,
} from 'lucide-react';
import api from '../lib/api';
import { ArenaControl } from './ChampionsArena';

const NIVEIS = [
  { id: 'facil', label: 'Fácil', color: 'bg-green-100 text-green-700' },
  { id: 'medio', label: 'Médio', color: 'bg-yellow-100 text-yellow-700' },
  { id: 'dificil', label: 'Difícil', color: 'bg-orange-100 text-orange-700' },
  { id: 'campeao', label: 'Campeão', color: 'bg-red-100 text-red-700' },
];

const STATUSES = [
  { id: 'rascunho', label: 'Rascunho', color: 'bg-gray-100 text-gray-700' },
  { id: 'ativa', label: 'Ativa', color: 'bg-emerald-100 text-emerald-700' },
  { id: 'arquivada', label: 'Arquivada', color: 'bg-slate-200 text-slate-600' },
];

const emptyBattle = {
  titulo: '', descricao: '', segmento: '', turma_sugerida: '', disciplina: '',
  nivel: 'medio', tempo_estimado: 30, pontuacao_total: 10, status: 'rascunho',
};

const emptyQuestion = {
  enunciado: '', alternativa_a: '', alternativa_b: '', alternativa_c: '', alternativa_d: '',
  resposta_correta: 'A', explicacao: '', pontuacao: 1, tempo_limite: 60,
};

export default function BancoDeBatalhas() {
  const [tab, setTab] = useState('lista');
  const [battles, setBattles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ status: '', q: '' });
  const [editor, setEditor] = useState(null); // { battle, questions, mode }
  const [applyDlg, setApplyDlg] = useState(null);
  const [arenaDlg, setArenaDlg] = useState(null); // { code, className }
  const [results, setResults] = useState([]);
  const [resultsFilter, setResultsFilter] = useState({ battle_id: '' });

  const loadBattles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.status) params.set('status', filter.status);
      if (filter.q) params.set('q', filter.q);
      const r = await api.get(`/champions/battles?${params}`);
      setBattles(r.data.items || []);
    } catch (e) {
      toast.error('Falha ao carregar batalhas');
    } finally { setLoading(false); }
  };

  const loadResults = async () => {
    try {
      const params = new URLSearchParams();
      if (resultsFilter.battle_id) params.set('battle_id', resultsFilter.battle_id);
      const r = await api.get(`/champions/results?${params}`);
      setResults(r.data.items || []);
    } catch { /* noop */ }
  };

  useEffect(() => { loadBattles(); /* eslint-disable-next-line */ }, [filter.status]);
  useEffect(() => { if (tab === 'historico') loadResults(); /* eslint-disable-next-line */ }, [tab, resultsFilter.battle_id]);

  const openCreate = () => setEditor({ battle: { ...emptyBattle }, questions: [], mode: 'create' });
  const openEdit = async (id) => {
    try {
      const r = await api.get(`/champions/battles/${id}`);
      setEditor({ battle: r.data.battle, questions: r.data.questions || [], mode: 'edit' });
    } catch { toast.error('Falha ao carregar batalha'); }
  };
  const closeEditor = () => setEditor(null);

  const saveBattle = async () => {
    if (!editor) return;
    const { battle, mode } = editor;
    if (!battle.titulo.trim()) { toast.error('Título é obrigatório.'); return; }
    try {
      if (mode === 'create') {
        const r = await api.post('/champions/battles', battle);
        toast.success('Batalha criada.');
        setEditor({ battle: r.data.battle, questions: [], mode: 'edit' });
      } else {
        await api.put(`/champions/battles/${battle.id}`, battle);
        toast.success('Batalha atualizada.');
      }
      loadBattles();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao salvar.'); }
  };

  const duplicate = async (id) => {
    try {
      await api.post(`/champions/battles/${id}/duplicate`);
      toast.success('Batalha duplicada.');
      loadBattles();
    } catch { toast.error('Falha ao duplicar.'); }
  };

  const archive = async (id) => {
    if (!window.confirm('Arquivar esta batalha?')) return;
    try {
      await api.delete(`/champions/battles/${id}`);
      toast.success('Batalha arquivada.');
      loadBattles();
    } catch { toast.error('Falha ao arquivar.'); }
  };

  const startArena = async (b) => {
    try {
      const r = await api.post('/champions/arena/sessions', {
        battle_id: b.id,
        class_name: b.turma_sugerida || '',
      });
      setArenaDlg({ code: r.data.session.code, battle_titulo: b.titulo });
      toast.success(`Arena criada — sala #${r.data.session.code}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao iniciar arena.');
    }
  };

  // ── Questões ──
  const addQuestion = async () => {
    if (!editor?.battle?.id) { toast.error('Salve a batalha antes de adicionar questões.'); return; }
    try {
      const r = await api.post(`/champions/battles/${editor.battle.id}/questions`, emptyQuestion);
      setEditor((s) => ({ ...s, questions: [...s.questions, r.data.question] }));
    } catch { toast.error('Falha ao adicionar questão.'); }
  };
  const updateQuestionField = (idx, patch) => {
    setEditor((s) => {
      const qs = [...s.questions];
      qs[idx] = { ...qs[idx], ...patch };
      return { ...s, questions: qs };
    });
  };
  const saveQuestion = async (idx) => {
    const q = editor.questions[idx];
    try {
      await api.put(`/champions/battles/${editor.battle.id}/questions/${q.id}`, q);
      toast.success('Questão salva.');
    } catch { toast.error('Falha ao salvar questão.'); }
  };
  const deleteQuestion = async (idx) => {
    const q = editor.questions[idx];
    if (!window.confirm('Excluir esta questão?')) return;
    try {
      await api.delete(`/champions/battles/${editor.battle.id}/questions/${q.id}`);
      setEditor((s) => ({ ...s, questions: s.questions.filter((_, i) => i !== idx) }));
    } catch { toast.error('Falha ao excluir.'); }
  };

  // ── Aplicar ──
  const openApply = (battle) => setApplyDlg({
    battle, class_id: '', class_name: '', student_ids_text: '',
    data_inicio: '', data_limite: '', permitir_repetir: false, mostrar_resultado: true, submitting: false,
  });
  const confirmApply = async () => {
    const a = applyDlg;
    if (!a.class_id && !a.student_ids_text.trim()) {
      toast.error('Informe a turma ou IDs de alunos.');
      return;
    }
    setApplyDlg((s) => ({ ...s, submitting: true }));
    try {
      const student_ids = a.student_ids_text.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);
      const r = await api.post(`/champions/battles/${a.battle.id}/apply`, {
        class_id: a.class_id || null,
        class_name: a.class_name || null,
        student_ids,
        data_inicio: a.data_inicio || null,
        data_limite: a.data_limite || null,
        permitir_repetir: a.permitir_repetir,
        mostrar_resultado: a.mostrar_resultado,
      });
      toast.success(`Aplicada para ${r.data.assigned_count} aluno(s).`);
      setApplyDlg(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao aplicar.');
      setApplyDlg((s) => ({ ...s, submitting: false }));
    }
  };

  return (
    <div className="space-y-4">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 max-w-md">
          <TabsTrigger value="lista" className="gap-2" data-testid="champ-tab-lista"><Library className="w-4 h-4" /> Banco</TabsTrigger>
          <TabsTrigger value="historico" className="gap-2" data-testid="champ-tab-historico"><History className="w-4 h-4" /> Histórico</TabsTrigger>
        </TabsList>

        {/* Lista de batalhas */}
        <TabsContent value="lista" className="space-y-3 pt-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              placeholder="Buscar por título..."
              value={filter.q}
              onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && loadBattles()}
              className="max-w-xs"
              data-testid="champ-search"
            />
            <select
              className="border rounded-md px-2 py-2 text-sm bg-white"
              value={filter.status}
              onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
              data-testid="champ-filter-status"
            >
              <option value="">Todos os status</option>
              {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
            <Button variant="outline" onClick={loadBattles} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Atualizar'}
            </Button>
            <div className="flex-1" />
            <Button onClick={openCreate} className="bg-red-600 hover:bg-red-700 text-white gap-2" data-testid="champ-new-btn">
              <Plus className="w-4 h-4" /> Nova batalha
            </Button>
          </div>

          {battles.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-slate-500">
              {loading ? 'Carregando…' : 'Nenhuma batalha encontrada. Crie a primeira pra começar seu banco.'}
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {battles.map((b) => {
                const nivel = NIVEIS.find((n) => n.id === b.nivel) || NIVEIS[1];
                const status = STATUSES.find((s) => s.id === b.status) || STATUSES[0];
                return (
                  <Card key={b.id} className="hover:shadow-lg transition">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between gap-2">
                        <CardTitle className="text-base">{b.titulo}</CardTitle>
                        <Badge className={status.color}>{status.label}</Badge>
                      </div>
                      <div className="flex flex-wrap gap-1 text-[11px] mt-1">
                        {b.disciplina && <Badge variant="outline">{b.disciplina}</Badge>}
                        {b.segmento && <Badge variant="outline">{b.segmento}</Badge>}
                        {b.turma_sugerida && <Badge variant="outline">{b.turma_sugerida}</Badge>}
                        <Badge className={nivel.color}>{nivel.label}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="text-xs text-slate-600 line-clamp-2">{b.descricao || '—'}</div>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><ListChecks className="w-3.5 h-3.5" /> {b.questions_count || 0} questões</span>
                        <span className="flex items-center gap-1"><Trophy className="w-3.5 h-3.5" /> {b.pontuacao_total} pts</span>
                      </div>
                      <div className="flex flex-wrap gap-1 pt-2">
                        <Button size="sm" variant="outline" className="gap-1" onClick={() => openEdit(b.id)} data-testid={`champ-edit-${b.id}`}>
                          <Edit2 className="w-3.5 h-3.5" /> Editar
                        </Button>
                        <Button size="sm" variant="outline" className="gap-1" onClick={() => duplicate(b.id)} data-testid={`champ-dup-${b.id}`}>
                          <Copy className="w-3.5 h-3.5" /> Duplicar
                        </Button>
                        <Button size="sm" className="gap-1 bg-rose-600 hover:bg-rose-700 text-white" onClick={() => startArena(b)} data-testid={`champ-arena-${b.id}`}>
                          <Zap className="w-3.5 h-3.5" /> Arena Ao Vivo
                        </Button>
                        <Button size="sm" className="gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => openApply(b)} data-testid={`champ-apply-${b.id}`}>
                          <Send className="w-3.5 h-3.5" /> Aplicar
                        </Button>
                        {b.status !== 'arquivada' && (
                          <Button size="sm" variant="outline" className="gap-1 text-slate-600" onClick={() => archive(b.id)}>
                            <Archive className="w-3.5 h-3.5" /> Arquivar
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Histórico */}
        <TabsContent value="historico" className="space-y-3 pt-3">
          <div className="flex items-center gap-2 flex-wrap">
            <select
              className="border rounded-md px-2 py-2 text-sm bg-white max-w-xs"
              value={resultsFilter.battle_id}
              onChange={(e) => setResultsFilter({ battle_id: e.target.value })}
              data-testid="champ-results-battle"
            >
              <option value="">Todas as batalhas</option>
              {battles.map((b) => <option key={b.id} value={b.id}>{b.titulo}</option>)}
            </select>
            <Button variant="outline" onClick={loadResults}>Atualizar</Button>
          </div>
          {results.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-slate-500">Nenhum registro de aplicação ainda.</CardContent></Card>
          ) : (
            <Card><CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                    <tr>
                      <th className="text-left p-2">Aluno</th>
                      <th className="text-left p-2">Turma</th>
                      <th className="text-left p-2">Batalha</th>
                      <th className="text-left p-2">Data início</th>
                      <th className="text-left p-2">Data limite</th>
                      <th className="text-left p-2">Pontuação</th>
                      <th className="text-left p-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r) => (
                      <tr key={r.id} className="border-t" data-testid={`champ-result-row-${r.id}`}>
                        <td className="p-2">{r.student_name}</td>
                        <td className="p-2">{r.class_name || '—'}</td>
                        <td className="p-2">{r.battle_titulo}</td>
                        <td className="p-2">{r.data_inicio || '—'}</td>
                        <td className="p-2">{r.data_limite || '—'}</td>
                        <td className="p-2 font-mono">{r.pontuacao ?? 0}</td>
                        <td className="p-2">
                          <Badge className={
                            r.status === 'concluida' ? 'bg-emerald-100 text-emerald-700' :
                            r.status === 'em_andamento' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-600'
                          }>{r.status}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent></Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Editor de Batalha + Questões */}
      <Dialog open={!!editor} onOpenChange={(o) => { if (!o) closeEditor(); }}>
        <DialogContent className="max-w-4xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-red-500" />
              {editor?.mode === 'create' ? 'Nova batalha' : `Editar: ${editor?.battle?.titulo || ''}`}
            </DialogTitle>
          </DialogHeader>
          {editor && (
            <div className="space-y-4">
              {/* Dados gerais */}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs">Título *</Label>
                  <Input value={editor.battle.titulo} onChange={(e) => setEditor((s) => ({ ...s, battle: { ...s.battle, titulo: e.target.value } }))} data-testid="champ-edit-titulo" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Descrição</Label>
                  <textarea
                    className="w-full border rounded-md p-2 text-sm"
                    rows={2}
                    value={editor.battle.descricao}
                    onChange={(e) => setEditor((s) => ({ ...s, battle: { ...s.battle, descricao: e.target.value } }))}
                  />
                </div>
                <div>
                  <Label className="text-xs">Segmento</Label>
                  <Input value={editor.battle.segmento} onChange={(e) => setEditor((s) => ({ ...s, battle: { ...s.battle, segmento: e.target.value } }))} placeholder="Fundamental II" />
                </div>
                <div>
                  <Label className="text-xs">Turma sugerida</Label>
                  <Input value={editor.battle.turma_sugerida} onChange={(e) => setEditor((s) => ({ ...s, battle: { ...s.battle, turma_sugerida: e.target.value } }))} placeholder="6º Ano A" />
                </div>
                <div>
                  <Label className="text-xs">Disciplina</Label>
                  <Input value={editor.battle.disciplina} onChange={(e) => setEditor((s) => ({ ...s, battle: { ...s.battle, disciplina: e.target.value } }))} placeholder="Matemática" />
                </div>
                <div>
                  <Label className="text-xs">Nível</Label>
                  <select className="w-full border rounded-md px-2 py-2 text-sm bg-white" value={editor.battle.nivel}
                          onChange={(e) => setEditor((s) => ({ ...s, battle: { ...s.battle, nivel: e.target.value } }))}>
                    {NIVEIS.map((n) => <option key={n.id} value={n.id}>{n.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Tempo estimado (min)</Label>
                  <Input type="number" min="0" value={editor.battle.tempo_estimado || 0}
                         onChange={(e) => setEditor((s) => ({ ...s, battle: { ...s.battle, tempo_estimado: Number(e.target.value) } }))} />
                </div>
                <div>
                  <Label className="text-xs">Pontuação total</Label>
                  <Input type="number" min="0" value={editor.battle.pontuacao_total || 0}
                         onChange={(e) => setEditor((s) => ({ ...s, battle: { ...s.battle, pontuacao_total: Number(e.target.value) } }))} />
                </div>
                <div>
                  <Label className="text-xs">Status</Label>
                  <select className="w-full border rounded-md px-2 py-2 text-sm bg-white" value={editor.battle.status}
                          onChange={(e) => setEditor((s) => ({ ...s, battle: { ...s.battle, status: e.target.value } }))}
                          data-testid="champ-edit-status">
                    {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={saveBattle} className="bg-red-600 hover:bg-red-700 text-white gap-2" data-testid="champ-save-battle">
                  <FileText className="w-4 h-4" /> {editor.mode === 'create' ? 'Salvar batalha' : 'Salvar alterações'}
                </Button>
              </div>

              {/* Questões */}
              {editor.battle.id && (
                <div className="space-y-2 border-t pt-3">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold flex items-center gap-2"><ListChecks className="w-4 h-4" /> Questões ({editor.questions.length})</div>
                    <Button size="sm" variant="outline" onClick={addQuestion} data-testid="champ-add-q"><Plus className="w-3.5 h-3.5" /> Adicionar questão</Button>
                  </div>
                  {editor.questions.length === 0 && <div className="text-xs text-slate-500">Nenhuma questão ainda. Adicione a primeira.</div>}
                  {editor.questions.map((q, idx) => (
                    <Card key={q.id}>
                      <CardContent className="p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-slate-500">Questão {idx + 1}</div>
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" onClick={() => saveQuestion(idx)}>Salvar</Button>
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteQuestion(idx)}><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        </div>
                        <textarea className="w-full border rounded-md p-2 text-sm" rows={2}
                                  placeholder="Enunciado"
                                  value={q.enunciado}
                                  onChange={(e) => updateQuestionField(idx, { enunciado: e.target.value })} />
                        <div className="grid grid-cols-2 gap-2">
                          {['a', 'b', 'c', 'd'].map((letter) => (
                            <div key={letter} className="flex items-center gap-2">
                              <span className="font-bold text-xs w-4">{letter.toUpperCase()}.</span>
                              <Input value={q[`alternativa_${letter}`] || ''}
                                     onChange={(e) => updateQuestionField(idx, { [`alternativa_${letter}`]: e.target.value })} />
                            </div>
                          ))}
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div>
                            <Label className="text-[11px]">Resposta correta</Label>
                            <select className="w-full border rounded px-2 py-1 bg-white" value={q.resposta_correta || 'A'}
                                    onChange={(e) => updateQuestionField(idx, { resposta_correta: e.target.value })}>
                              {['A', 'B', 'C', 'D'].map((l) => <option key={l} value={l}>{l}</option>)}
                            </select>
                          </div>
                          <div>
                            <Label className="text-[11px]">Pontuação</Label>
                            <Input type="number" min="0" step="0.5" value={q.pontuacao || 0}
                                   onChange={(e) => updateQuestionField(idx, { pontuacao: Number(e.target.value) })} />
                          </div>
                          <div>
                            <Label className="text-[11px]">Tempo limite (s)</Label>
                            <Input type="number" min="0" value={q.tempo_limite || 0}
                                   onChange={(e) => updateQuestionField(idx, { tempo_limite: Number(e.target.value) })} />
                          </div>
                        </div>
                        <textarea className="w-full border rounded-md p-2 text-xs"
                                  rows={1}
                                  placeholder="Explicação (opcional)"
                                  value={q.explicacao || ''}
                                  onChange={(e) => updateQuestionField(idx, { explicacao: e.target.value })} />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeEditor}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Aplicar */}
      <Dialog open={!!applyDlg} onOpenChange={(o) => { if (!o) setApplyDlg(null); }}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <Send className="w-5 h-5" /> Aplicar batalha
            </DialogTitle>
          </DialogHeader>
          {applyDlg && (
            <div className="space-y-3">
              <div className="text-sm text-slate-700">
                <strong>{applyDlg.battle.titulo}</strong>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">ID da turma</Label>
                  <Input value={applyDlg.class_id} onChange={(e) => setApplyDlg((s) => ({ ...s, class_id: e.target.value }))} placeholder="ex: 6a-2026" data-testid="champ-apply-classid" />
                </div>
                <div>
                  <Label className="text-xs">Nome da turma</Label>
                  <Input value={applyDlg.class_name} onChange={(e) => setApplyDlg((s) => ({ ...s, class_name: e.target.value }))} placeholder="6º Ano A" />
                </div>
              </div>
              <div>
                <Label className="text-xs">IDs de alunos específicos (opcional, separados por vírgula)</Label>
                <textarea className="w-full border rounded-md p-2 text-xs" rows={2}
                          value={applyDlg.student_ids_text}
                          onChange={(e) => setApplyDlg((s) => ({ ...s, student_ids_text: e.target.value }))}
                          placeholder="id1, id2, id3"
                          data-testid="champ-apply-students" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Data de início</Label>
                  <Input type="date" value={applyDlg.data_inicio} onChange={(e) => setApplyDlg((s) => ({ ...s, data_inicio: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Data limite</Label>
                  <Input type="date" value={applyDlg.data_limite} onChange={(e) => setApplyDlg((s) => ({ ...s, data_limite: e.target.value }))} />
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={applyDlg.permitir_repetir} onChange={(e) => setApplyDlg((s) => ({ ...s, permitir_repetir: e.target.checked }))} /> Permitir repetir tentativa
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={applyDlg.mostrar_resultado} onChange={(e) => setApplyDlg((s) => ({ ...s, mostrar_resultado: e.target.checked }))} /> Mostrar resultado ao aluno
                </label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyDlg(null)} disabled={applyDlg?.submitting}>Cancelar</Button>
            <Button onClick={confirmApply} disabled={applyDlg?.submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid="champ-apply-confirm">
              {applyDlg?.submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Aplicar batalha
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Modal Arena Ao Vivo */}
      <Dialog open={!!arenaDlg} onOpenChange={(o) => { if (!o) setArenaDlg(null); }}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600">
              <Zap className="w-5 h-5" /> Arena Ao Vivo · {arenaDlg?.battle_titulo}
            </DialogTitle>
          </DialogHeader>
          {arenaDlg && <ArenaControl code={arenaDlg.code} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setArenaDlg(null)}>Fechar painel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
